/**
 * Rider settlements.
 *
 * Every settlement shows both directions. "CenoHub owes ₦3,500" and "the rider
 * owes ₦1,000" are facts about different people, and the single ₦2,500 a
 * netted figure would show tells a dispatcher neither of them — so the two sit
 * side by side and the netted position is a third, separate line.
 *
 * Paginated because the list grows with every settled day. Nothing is summed in
 * the browser: the totals strip comes from its own aggregate endpoint, since
 * adding up a page would understate what is owed as soon as there were two.
 */

import { useState, type ReactNode } from 'react';
import {
  Badge, Button, Card, EmptyState, ErrorState, Pagination, Select, Spinner, TableSkeleton,
} from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { date, money } from '../../lib/format';
import { Line, SectionTitle, Stat, TwoWay } from './parts';
import { useRiders, useSettlementTotals, useSettlements, type Settlement } from './data';

const STATUS_TONE: Record<Settlement['status'], 'neutral' | 'success' | 'warning'> = {
  unpaid: 'warning',
  paid: 'success',
  void: 'neutral',
};

export function LogisticsSettlementsPage(): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const timezone = business?.timezone ?? 'Africa/Lagos';

  const [status, setStatus] = useState('');
  const [riderId, setRiderId] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const filters = {
    status: status || undefined,
    riderId: riderId || undefined,
    page,
    pageSize,
  };
  const list = useSettlements(filters);
  const totals = useSettlementTotals({ status: status || undefined, riderId: riderId || undefined });
  const riders = useRiders();

  const [openId, setOpenId] = useState<string | null>(null);

  if (list.isError) {
    return <ErrorState message="Could not load settlements." onRetry={() => void list.refetch()} />;
  }

  const rows = list.data?.data ?? [];

  return (
    <div className="space-y-5">
      <section>
        <SectionTitle>Where things stand</SectionTitle>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          <Stat
            label="CenoHub owes riders"
            value={totals.data?.cenohubOwesRider}
            currency={currency}
            tone="good"
          />
          <Stat
            label="Riders owe CenoHub"
            value={totals.data?.riderOwesCenohub}
            currency={currency}
            tone="owed"
          />
          <Stat label="Remittance" value={totals.data?.remittance} currency={currency} />
          <Stat
            label="Final net position"
            value={totals.data?.finalNetPosition}
            currency={currency}
            hint="both directions, netted"
          />
        </div>
      </section>

      <div className="flex flex-wrap items-end gap-2.5">
        <Select
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          options={[
            { value: '', label: 'All' },
            { value: 'unpaid', label: 'Unpaid' },
            { value: 'paid', label: 'Paid' },
            { value: 'void', label: 'Void' },
          ]}
        />
        <Select
          label="Rider"
          value={riderId}
          onChange={(e) => {
            setRiderId(e.target.value);
            setPage(1);
          }}
          options={[
            { value: '', label: 'All riders' },
            ...(riders.data?.data ?? []).map((r) => ({ value: r.id, label: r.canonicalName })),
          ]}
        />
      </div>

      {list.isPending ? (
        <TableSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No settlements yet"
          description="A settlement is built for a rider and a date, covering the completed deliveries that no other settlement has taken."
        />
      ) : (
        <>
          <ul className="space-y-2.5">
            {rows.map((s) => (
              <li key={s.id}>
                <Card>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{s.riderName}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {s.settlementDate}
                        {' · '}
                        {s.rides} {s.rides === 1 ? 'ride' : 'rides'}
                        {s.branches.length ? ` · ${s.branches.join(', ')}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone={STATUS_TONE[s.status]}>{s.status}</Badge>
                      <Button
                        variant="ghost"
                        onClick={() => setOpenId(openId === s.id ? null : s.id)}
                        aria-expanded={openId === s.id}
                      >
                        {openId === s.id ? 'Hide' : 'Detail'}
                      </Button>
                    </div>
                  </div>

                  <div className="mt-3">
                    <TwoWay
                      owedToRider={s.cenohubOwesRider}
                      owedToCenohub={s.riderOwesCenohub}
                      currency={currency}
                    />
                  </div>

                  {openId === s.id ? (
                    <div className="mt-3 border-t border-slate-100 pt-2">
                      <Line
                        label="Rider total earnings"
                        value={money(s.riderTotalEarnings, currency)}
                      />
                      <Line label="Cash collected by rider" value={money(s.cashCollectedByRider, currency)} />
                      <Line
                        label="Rider payout already held"
                        value={money(s.cashRiderPayoutHeld, currency)}
                        note="in that cash"
                      />
                      <Line
                        label="Company share held"
                        value={money(s.companyShareHeldByRider, currency)}
                        note="owed back to CenoHub"
                        tone="owed"
                      />
                      <Line label="Remittance" value={money(s.remittance, currency)} />
                      <div className="my-1 border-t border-dashed border-slate-200" />
                      <Line
                        label="CenoHub owes rider"
                        value={money(s.cenohubOwesRider, currency)}
                        tone="good"
                        strong
                      />
                      <Line
                        label="Rider owes CenoHub"
                        value={money(s.riderOwesCenohub, currency)}
                        tone="owed"
                        strong
                      />
                      <Line
                        label="Final net position"
                        value={money(s.finalNetPosition, currency)}
                        tone={Number(s.finalNetPosition) < 0 ? 'owed' : 'good'}
                        strong
                      />
                      {s.status === 'paid' ? (
                        <p className="mt-2 text-xs text-slate-500">
                          Paid {s.paidAt ? date(s.paidAt, timezone) : ''}
                          {s.paymentMethod ? ` · ${s.paymentMethod}` : ''}
                          {s.paymentReference ? ` · ${s.paymentReference}` : ''}
                        </p>
                      ) : (
                        <p className="mt-2 text-xs text-slate-400">
                          Payment is recorded by hand for now.
                        </p>
                      )}
                    </div>
                  ) : null}
                </Card>
              </li>
            ))}
          </ul>

          <Pagination
            page={list.data?.page ?? 1}
            pageSize={list.data?.pageSize ?? pageSize}
            total={list.data?.total ?? 0}
            onPageChange={setPage}
          />
        </>
      )}

      {list.isFetching && !list.isPending ? (
        <div className="flex justify-center py-2">
          <Spinner />
        </div>
      ) : null}
    </div>
  );
}
